import { createLogger } from "../helper/logger.js";
import "./speech-model.js";
import { filterVoicesByLocale, normalizeSpeakerSettings, pickVoice, splitIntoChunks } from "./speech-utils.js";
//#region src/speech/SpeechService.ts
/**
* SpeechService — text-to-speech through the browser's Web Speech API.
*
* What it fixes compared to the usual ad-hoc implementation:
* - Voices load asynchronously and differently per engine: `getVoices()` is
*   empty at first in Chrome, Safari sometimes never fires `voiceschanged`.
*   `loadVoices()` combines the immediate list, the event and a short poll.
* - The configured voice is stored as `voiceURI` and resolved on every
*   utterance; a missing voice falls back to the best one for the locale
*   instead of going silent.
* - Mobile Safari/Chrome only speak after a user gesture. The service unlocks
*   itself on the first pointer/key gesture and keeps the last announcement
*   that arrived before it (a few seconds), so a server event right after
*   start is not lost.
* - One queue with priorities instead of `cancel()` before every call —
*   which in Chrome tends to kill the following utterance as well.
* - Utterances are referenced until they end (Chrome garbage-collects
*   unreferenced ones mid-sentence) and long text is split into chunks
*   (Chrome stops after roughly 15 seconds).
* - Speech stops when the page is hidden; Chrome would otherwise pause and
*   replay everything at once on return.
*
* Store-free: enabling, speakers and locale come from the app's settings.
*
* @example
* ```typescript
* speechService.setLocale("de");
* speechService.configureSpeaker("player", { voiceURI, rate: 1, pitch: 1, volume: 1 });
* speechService.setEnabled(true);
* void speechService.loadVoices();                    // e.g. non-blocking init task
* speechService.say("Du bist dran", { speaker: "player", priority: "interrupt" });
* ```
*/
var logger = createLogger("SpeechService");
var VOICES_POLL_INTERVAL_MS = 250;
var VOICES_POLL_MAX_MS = 3e3;
var SpeechService = class {
	constructor() {
		this._enabled = false;
		this._locale = "en";
		this._speakers = /* @__PURE__ */ new Map();
		this._voices = [];
		this._unlocked = false;
		this._listenersInstalled = false;
		this._queue = [];
		this._speaking = false;
		this._inFlight = /* @__PURE__ */ new Set();
	}
	/** Web Speech API available? (SSR/tests: false — every call becomes a no-op.) */
	get isSupported() {
		return typeof window !== "undefined" && "speechSynthesis" in window && "SpeechSynthesisUtterance" in window;
	}
	/** At least one voice for the current locale is known. */
	get isReady() {
		return filterVoicesByLocale(this._voices, this._locale).length > 0;
	}
	/** A user gesture has happened (or `unlock()` was called inside one). */
	get isUnlocked() {
		return this._unlocked;
	}
	get enabled() {
		return this._enabled;
	}
	/** Switching off stops whatever is being spoken. */
	setEnabled(enabled) {
		this._enabled = enabled && this.isSupported;
		if (!this._enabled) this.stop();
	}
	/** Language for voice selection ("de", "en-GB", …). */
	setLocale(locale) {
		this._locale = locale;
	}
	/** Register or update a speaker; values are clamped to the supported ranges. */
	configureSpeaker(id, settings) {
		this._speakers.set(id, normalizeSpeakerSettings(settings));
	}
	getSpeaker(id) {
		return this._speakers.get(id);
	}
	/**
	* Load the engine's voices: immediately if present, otherwise via
	* `voiceschanged` plus a short poll (Safari). Resolves with whatever is
	* known after at most a few seconds — never rejects. Also installs the
	* gesture unlock and the visibility handling.
	*/
	loadVoices() {
		if (!this.isSupported) return Promise.resolve([]);
		this._installListeners();
		if (this._voices.length) return Promise.resolve(this._voices);
		if (this._voicesLoading) return this._voicesLoading;
		this._voicesLoading = new Promise((resolve) => {
			const synth = window.speechSynthesis;
			const started = Date.now();
			const finish = () => {
				synth.removeEventListener("voiceschanged", check);
				window.clearInterval(timer);
				this._voicesLoading = void 0;
				logger.debug(`Voices loaded: ${this._voices.length}`);
				resolve(this._voices);
			};
			const check = () => {
				const voices = synth.getVoices();
				if (voices.length) {
					this._voices = voices;
					finish();
				} else if (Date.now() - started >= VOICES_POLL_MAX_MS) finish();
			};
			synth.addEventListener("voiceschanged", check);
			const timer = window.setInterval(check, VOICES_POLL_INTERVAL_MS);
			check();
		});
		return this._voicesLoading;
	}
	get locale() {
		return this._locale;
	}
	/** Known voices, filtered and ordered for the (given or current) locale. */
	getVoices(locale = this._locale) {
		return filterVoicesByLocale(this._voices, locale);
	}
	/** Every known voice, any language — for pickers that offer foreign voices on purpose. */
	getAllVoices() {
		return [...this._voices];
	}
	/**
	* Speak text for a speaker. No-op when unsupported or disabled. Before the
	* first gesture the announcement is kept and spoken after the unlock if
	* it is still fresh (`SPEECH_PENDING_MAX_AGE_MS`).
	*/
	say(text, options = {}) {
		if (!this._enabled || !text.trim()) return;
		const priority = options.priority ?? "queue";
		const settings = options.speaker && this._speakers.get(options.speaker) || normalizeSpeakerSettings();
		if (!this._unlocked) {
			this._installListeners();
			this._pending = {
				text,
				settings,
				priority,
				at: Date.now()
			};
			logger.debug("Speech kept until the first user gesture");
			return;
		}
		this._enqueue({
			text,
			settings
		}, priority);
	}
	/** Cancel current and queued speech. */
	stop() {
		this._queue = [];
		this._pending = void 0;
		if (!this.isSupported) return;
		this._inFlight.clear();
		this._speaking = false;
		window.speechSynthesis.cancel();
	}
	/**
	* Mark the engine usable — call inside a user gesture (e.g. the "test
	* voice" button); the first pointer/key gesture does it automatically.
	* Speaks an empty utterance so mobile Safari accepts later speech.
	*/
	unlock() {
		if (this._unlocked || !this.isSupported) return;
		this._unlocked = true;
		window.speechSynthesis.speak(new SpeechSynthesisUtterance(""));
		const pending = this._pending;
		this._pending = void 0;
		if (pending && Date.now() - pending.at <= 5e3) this._enqueue(pending, pending.priority);
	}
	_installListeners() {
		if (this._listenersInstalled || !this.isSupported) return;
		this._listenersInstalled = true;
		const unlock = () => this.unlock();
		for (const type of ["pointerdown", "keydown"]) window.addEventListener(type, unlock, {
			once: true,
			passive: true,
			capture: true
		});
		document.addEventListener("visibilitychange", () => {
			if (document.visibilityState === "hidden") this.stop();
		});
	}
	_enqueue(announcement, priority) {
		switch (priority) {
			case "interrupt":
				this.stop();
				this._queue.push(announcement);
				break;
			case "queue":
				this._queue.push(announcement);
				break;
			case "idle":
				if (this._speaking || this._queue.length) return;
				this._queue.push(announcement);
		}
		this._speakNext();
	}
	_speakNext() {
		if (this._speaking) return;
		const next = this._queue.shift();
		if (!next) return;
		const chunks = splitIntoChunks(next.text, 180);
		const voice = pickVoice(this._voices, this._locale, next.settings.voiceURI);
		const synth = window.speechSynthesis;
		this._speaking = true;
		let remaining = chunks.length;
		const done = (utterance) => {
			if (!this._inFlight.delete(utterance)) return;
			if (--remaining > 0) return;
			this._speaking = false;
			this._speakNext();
		};
		for (const chunk of chunks) {
			const utterance = new SpeechSynthesisUtterance(chunk);
			if (voice) utterance.voice = voice;
			utterance.lang = voice?.lang ?? this._locale;
			utterance.rate = next.settings.rate;
			utterance.pitch = next.settings.pitch;
			utterance.volume = next.settings.volume;
			utterance.onend = () => done(utterance);
			utterance.onerror = (e) => {
				if (e.error !== "interrupted" && e.error !== "canceled") logger.warn(`Speech failed: ${e.error}`);
				done(utterance);
			};
			this._inFlight.add(utterance);
			synth.speak(utterance);
		}
	}
};
var speechService = new SpeechService();
//#endregion
export { SpeechService, speechService };
